import { useEffect, useMemo, useState } from "react";
import { activeChildren, activeRoots, type ActiveEntry } from "../lib/active";
import { GROUPS, groupOfKind, KIND_LABEL } from "../lib/colors";
import { CT_UCDP, type Conflicts } from "../lib/data";
import { fmtDeaths, fmtInt, plural } from "../lib/format";
import { useApp } from "../lib/store";
import { formatInstant, yearsLabel } from "../lib/time";
import { ConflictDetail } from "./ConflictDetail";
import { EventCard } from "./EventCard";
import { NowCard } from "./NowCard";
import { ReplayPanel } from "./Replays";

const PAGE = 25;

export function SidePanel() {
  const conflicts = useApp((s) => s.conflicts);
  const selected = useApp((s) => s.selected);
  const event = useApp((s) => s.event);
  const t = useApp((s) => s.t);
  const span = useApp((s) => s.span);
  const now = useApp((s) => s.now);
  const kinds = useApp((s) => s.kinds);
  const sheet = useApp((s) => s.sheet);
  const setSheet = useApp((s) => s.setSheet);
  const replaying = useApp((s) => !!s.replay);
  const pickerOpen = useApp((s) => s.panel === "replays"); // the war picker covers the panel and is modal
  useEffect(() => {
    // a replay keeps the map in view on phones; choosing a war or a battle otherwise opens the sheet
    if ((selected >= 0 || event) && sheet === "collapsed" && !useApp.getState().replay) setSheet("half");
  }, [selected, event]); // eslint-disable-line react-hooks/exhaustive-deps
  const tBucket = Math.floor(t * (span < 3 ? 365 : span < 40 ? 12 : 1)) / (span < 3 ? 365 : span < 40 ? 12 : 1);
  const active = useMemo(() => (conflicts ? activeRoots(conflicts, t, kinds) : []), [conflicts, tBucket, kinds]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!conflicts) return <aside className="panel" />;
  const nearNow = now - t < 1.05;
  return (
    <aside className={`panel sheet-${sheet}`} aria-label="Conflicts at this moment" inert={pickerOpen}>
      <button className="sheet-handle" onClick={() => setSheet(sheet === "full" ? "collapsed" : sheet === "half" ? "full" : "half")} aria-label="Resize panel">
        <span />
      </button>
      <header className="panel-head">
        <div className="panel-date" data-testid="playhead-date">{formatInstant(t, span)}</div>
        <div className="panel-sub">
          {active.length === 0 ? "No recorded conflict at this moment" : `${plural(active.length, "conflict")} going on`}
          {nearNow && <span className="badge-now">today</span>}
        </div>
      </header>
      <div className="panel-body">
        {event && <EventCard />}
        {replaying && <ReplayPanel />}
        {selected >= 0 ? (
          <ConflictDetail key={selected} index={selected} />
        ) : (
          <>
            {nearNow && <NowCard active={active} />}
            <ActiveList entries={active} c={conflicts} t={t} />
          </>
        )}
      </div>
    </aside>
  );
}

function ActiveList({ entries, c, t }: { entries: ActiveEntry[]; c: Conflicts; t: number }) {
  const [shown, setShown] = useState(PAGE);
  const select = useApp((s) => s.select);
  const setHoverConflict = useApp((s) => s.setHoverConflict);
  const hover = useApp((s) => s.hoverConflict);
  if (!entries.length) {
    return (
      <p className="empty">
        Nothing in the record for this moment. Data before 1500 are sparse, so scroll the timeline or pick an era above.
      </p>
    );
  }
  return (
    <>
      <ol className="active-list" data-testid="active-list">
        {entries.slice(0, shown).map((e) => {
          const i = e.i, t0 = c.t0[i], t1 = c.t1[i];
          const progress = t1 > t0 ? Math.min(1, Math.max(0, (t - t0) / (t1 - t0))) : 1;
          const kids = c.ct[i] !== CT_UCDP ? activeChildren(c, i, t).filter((k) => c.ct[k] !== CT_UCDP).slice(0, 3) : [];
          return (
            <li key={i} className={hover === i ? "hovered" : ""} onMouseEnter={() => setHoverConflict(i)} onMouseLeave={() => setHoverConflict(-1)}>
              <button className="active-item" onClick={() => select(i, { fly: true })}>
                <span className="dot" style={{ background: GROUPS[groupOfKind(c.kind[i])].hex }} aria-hidden />
                <span className="ai-main">
                  <span className="ai-name">{c.name[i]}</span>
                  <span className="ai-meta">
                    {yearsLabel(t0, t1, !!c.og[i])}
                    {e.deathsYear != null && e.deathsYear > 0 ? ` · ${fmtDeaths(e.deathsYear)} deaths in ${Math.floor(t)}` : c.d[i] > 0 ? ` · ${fmtDeaths(c.d[i])} deaths` : ""}
                  </span>
                  {kids.length > 0 && <span className="ai-kids">{kids.map((k) => c.name[k]).join(" · ")}</span>}
                  <span className="ai-progress" aria-hidden><span style={{ width: `${progress * 100}%`, background: GROUPS[groupOfKind(c.kind[i])].hex }} /></span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      {entries.length > shown && (
        <button className="more" onClick={() => setShown(shown + 100)}>Show {fmtInt(Math.min(100, entries.length - shown))} more of {fmtInt(entries.length)}</button>
      )}
      <p className="fineprint">
        Ranked by prominence and, where measured, by deaths in that year. {KIND_LABEL.length > 0 && "Colours: "}
        {GROUPS.slice(0, 3).map((g) => g.label.toLowerCase()).join(", ")}.
      </p>
    </>
  );
}
